// src/components/TeachingPlanCard/parseTeachingPlanMarkdown.js

/**
 * Parse the Step 7 Markdown into:
 *  - intro (text before the first table)
 *  - table (the first markdown table as text, if needed later)
 *  - mluItems: [{ code, title, raw }]
 */
/**
 * Some models wrap the whole answer in a ```markdown ... ``` fence. Left in place,
 * the leading fence turns the intro into an unterminated code block (monospace text
 * that never wraps and overflows horizontally). Strip a fence that wraps the whole
 * response; leave partial/legitimate code fences untouched.
 */
function stripWrappingCodeFence(md) {
  const trimmed = md.trim();
  if (!trimmed.startsWith("```")) return md;

  let lines = trimmed.split("\n");
  lines = lines.slice(1); // drop opening ``` / ```markdown
  if (lines.length && lines[lines.length - 1].trim().startsWith("```")) {
    lines = lines.slice(0, -1); // drop closing fence if present
  }
  return lines.join("\n").trim();
}

export default function parseTeachingPlanMarkdown(md) {
  if (!md) {
    return { intro: "", table: "", mluItems: [] };
  }

  const lines = stripWrappingCodeFence(md).split("\n");
  let tableStart = -1;
  let tableEnd = -1;

  // Find first Markdown table (header + separator row)
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const next = lines[i + 1].trim();

    if (
      line.startsWith("|") &&
      line.includes("|") &&
      next.startsWith("|") &&
      next.includes("---")
    ) {
      tableStart = i;
      break;
    }
  }

  if (tableStart !== -1) {
    // Move forward until the table ends (line no longer starts with '|')
    for (let i = tableStart; i < lines.length; i++) {
      const line = lines[i];
      const isTableLine =
        line.trim().startsWith("|") && line.includes("|");

      if (!isTableLine) {
        tableEnd = i;
        break;
      }

      if (i === lines.length - 1) {
        tableEnd = lines.length;
      }
    }
  }

  const introLines =
    tableStart !== -1 ? lines.slice(0, tableStart) : lines;
  const tableLines =
    tableStart !== -1 && tableEnd > tableStart
      ? lines.slice(tableStart, tableEnd)
      : [];

  const intro = introLines.join("\n").trim();
  const table = tableLines.join("\n").trim();

  // Extract MLUs from the second column of each table row
  const mluItems = [];
  if (tableLines.length > 2) {
    for (let i = 2; i < tableLines.length; i++) {
      const row = tableLines[i].trim();
      if (!row.startsWith("|")) continue;

      // Split row into cells, dropping leading/trailing empty cells
      const cells = row
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());

      if (cells.length >= 2) {
        const mluCell = cells[1];
        if (!mluCell) continue;

        const cleaned = mluCell.replace(/[*_`]/g, "");

        // Try to capture exact format (e.g. TEC-101, EDU 203)
        const codeMatch = cleaned.match(/(EDU|EID|GOV|TEC)[-\s]?(\d{3})(.*)$/i);

        let code = cleaned.trim();
        let title = "";

        if (codeMatch) {
          code = `${codeMatch[1].toUpperCase()}-${codeMatch[2]}`;
          title = codeMatch[3].replace(/^[\s\-–:]+/, "").trim();
        } else {
          // Fallback: split on em-dash or colon only (avoid regular hyphen)
          const parts = cleaned.split(/\s*[–:]\s*/);
          if (parts.length > 1) {
            code = parts[0].trim().split(/\s+/)[0];
            title = parts.slice(1).join(" – ").trim();
          }
        }

        mluItems.push({
          code: code || cleaned,
          title,
          raw: cleaned,
        });
      }
    }
  }

  return { intro, table, mluItems };
}
